import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { chiama } from './cliente.js';

/** La scheda di un'anagrafica, con tutte le righe collegate. */
export interface Indirizzo {
  id: number;
  name: string;
  addressTypeId: number | null;
  addressType: string | null;
  countryId: number;
  country: string | null;
  address: string | null;
  addressExtra: string | null;
  city: string | null;
  postalCode: string | null;
  provinceState: string | null;
  bcLocationCode: string | null;
  destinationCode: string | null;
  isMain: boolean;
  archived: boolean;
  /** Le Business Central in cui c'è: `['SPA']`, `['INC']`, tutte e due o nessuna. */
  bc: string[];
}

export interface Contatto {
  id: number;
  firstName: string;
  lastName: string;
  role: string | null;
  officeName: string | null;
  archived: boolean;
}

export interface Recapito {
  id: number;
  contactId: number | null;
  kind: string | null;
  isMain: boolean;
  email?: string;
  phoneNumber?: string;
  url?: string;
  notifyEtaDeviation?: boolean;
  notifyStateDowngrade?: boolean;
  notifyPricelistApproval?: boolean;
}

export interface Scheda {
  id: number;
  type: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  bcCodeSpa: string | null;
  bcCodeInc: string | null;
  taxNumber: string | null;
  taxCardId: string | null;
  agentInfo: string | null;
  notes: string | null;
  archived: boolean;
  companies: { id: number; code: string; name: string }[];
  categories: { id: number; name: string }[];
  /** Da dove è arrivato il contatto: Fiera, Sito Web, Social… */
  tags: { id: number; name: string }[];
  addresses: Indirizzo[];
  /** Nella Inc, le destinazioni che non si vedono perché la BC della Inc non le ha. */
  addressesNotInBc: number;
  contacts: Contatto[];
  emails: Recapito[];
  phones: Recapito[];
  websites: Recapito[];
  preferences: {
    language: string;
    defaultPaymentTypeId: number | null;
    defaultBankId: number | null;
    defaultSectionalId: number | null;
    defaultCurrency: string | null;
    sendDataToCarriers: boolean;
    prepayment: boolean;
    automaticSupply: boolean;
    directTransaction: boolean;
    requestCurrency: boolean;
    pcqi: string | null;
  } | null;
}

export function useScheda(id: number) {
  return useQuery({
    queryKey: ['identita', id],
    queryFn: () => chiama<Scheda>(`/api/identities/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

/** I limiti dei campi verso Business Central, per i contatori. */
export function useLimiti() {
  return useQuery({
    queryKey: ['limiti-bc'],
    queryFn: () => chiama<{ limits: Record<string, number> }>('/api/identities/meta'),
    staleTime: 60 * 60_000,
  });
}

export function useSalvaAnagrafica(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Scheda>(`/api/identities/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (scheda) => {
      client.setQueryData(['identita', id], scheda);
      // l'elenco mostra ragione sociale e codici: va riletto
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

/**
 * Convertire: un lead che diventa prospect, un prospect che diventa cliente.
 *
 * Cambia l'elenco in cui la riga vive, quindi vanno riletti **tutti** gli
 * elenchi e non solo quello da cui si è partiti: la riga sparisce da uno e
 * compare in un altro.
 */
export function useConverti(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (type: string) =>
      chiama<Scheda>(`/api/identities/${id}/type`, { metodo: 'PATCH', corpo: { type } }),
    onSuccess: (scheda) => {
      client.setQueryData(['identita', id], scheda);
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

export function useSalvaIndirizzo(identityId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dati }: Record<string, unknown> & { id?: number }) =>
      id
        ? chiama<{ id: number }>(`/api/identities/${identityId}/addresses/${id}`, {
            metodo: 'PATCH',
            corpo: dati,
          })
        : chiama<{ id: number }>(`/api/identities/${identityId}/addresses`, {
            metodo: 'POST',
            corpo: dati,
          }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['identita', identityId] });
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

export function useEliminaIndirizzo(identityId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ deleted?: boolean; archived?: boolean; message?: string }>(
        `/api/identities/${identityId}/addresses/${id}`,
        { metodo: 'DELETE' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['identita', identityId] });
      void client.invalidateQueries({ queryKey: ['anagrafiche'] });
    },
  });
}

/** Le scelte per i menu della scheda: pagamenti, banche, sezionali, valute, categorie, tag. */
export interface OpzioniScheda {
  paymentTypes: { id: number; label: string }[];
  banks: { id: number; label: string }[];
  sectionals: { id: number; label: string }[];
  currencies: { id: string; label: string }[];
  categories: { id: number; label: string }[];
  tags: { id: number; label: string }[];
}

export function useOpzioni() {
  return useQuery({
    queryKey: ['opzioni-anagrafica'],
    queryFn: () => chiama<OpzioniScheda>('/api/identities/options'),
    staleTime: 10 * 60_000,
  });
}

export function useSalvaPreferenze(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Scheda>(`/api/identities/${id}/preferences`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: (scheda) => client.setQueryData(['identita', id], scheda),
  });
}

export function useSalvaCategorie(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (categoryIds: number[]) =>
      chiama<Scheda>(`/api/identities/${id}/categories`, { metodo: 'PUT', corpo: { categoryIds } }),
    onSuccess: (scheda) => client.setQueryData(['identita', id], scheda),
  });
}

export function useSalvaTag(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (tagIds: number[]) =>
      chiama<Scheda>(`/api/identities/${id}/tags`, { metodo: 'PUT', corpo: { tagIds } }),
    onSuccess: (scheda) => client.setQueryData(['identita', id], scheda),
  });
}

export function useSalvaContatto(identityId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dati }: Record<string, unknown> & { id?: number }) =>
      id
        ? chiama<{ id: number }>(`/api/identities/${identityId}/contacts/${id}`, {
            metodo: 'PATCH',
            corpo: dati,
          })
        : chiama<{ id: number }>(`/api/identities/${identityId}/contacts`, {
            metodo: 'POST',
            corpo: dati,
          }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['identita', identityId] }),
  });
}

/** Le tre tabelle dei recapiti hanno la stessa forma: un percorso le distingue. */
export type TipoRecapito = 'emails' | 'phones' | 'websites';

export function useSalvaRecapito(identityId: number, tipo: TipoRecapito) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...dati }: Record<string, unknown> & { id?: number }) =>
      id
        ? chiama<{ id: number }>(`/api/identities/${identityId}/${tipo}/${id}`, {
            metodo: 'PATCH',
            corpo: dati,
          })
        : chiama<{ id: number }>(`/api/identities/${identityId}/${tipo}`, {
            metodo: 'POST',
            corpo: dati,
          }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['identita', identityId] }),
  });
}

export function useEliminaRecapito(identityId: number, tipo: TipoRecapito) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ deleted: boolean }>(`/api/identities/${identityId}/${tipo}/${id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['identita', identityId] }),
  });
}

export function useEliminaContatto(identityId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ deleted: boolean }>(`/api/identities/${identityId}/contacts/${id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['identita', identityId] }),
  });
}
